import React from "react";

import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation } from "@apollo/client";
import { GET_PACKAGES_QUERY } from "../../graphql/queries/packages";
import { useEffect, useState } from "react";
import { GET_TOWERS_QUERY } from "../../graphql/queries/towers";
import { CREATE_CLIENT_MUTATION } from "../../graphql/mutations/createClientMutation";
import Loader from "../../components/loader/loader";
import { toastFunction, errorToastFunction } from "../../utils/toast";
import FloatingLabelInput from "../FloatingLabelInput/FloatingLabelInput";
import SingleSelect from "../single-select/single-select";
import DisplaySelect from "../display-select/display-select";

const AddClient = ({ onCancel }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {},
  });

  const getPackagesResponse = useQuery(GET_PACKAGES_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const getTowersResponse = useQuery(GET_TOWERS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [createClient, { loading }] = useMutation(CREATE_CLIENT_MUTATION);

  const [packages, setPackages] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [towers, setTowers] = useState(null);

  useEffect(() => {
    if (getPackagesResponse?.data) {
      const data = getPackagesResponse.data.packages.items.map((p) => ({
        value: p.id,
        label: p.name,
      }));
      setPackages(data);
    }

    if (getTowersResponse.data) {
      const data = getTowersResponse.data.towers.items.map((t) => ({
        value: t.zonkeId,
        label: t.zonkeId,
      }));

      setTowers(data);
    }
  }, [getPackagesResponse, getTowersResponse]);

  if (getPackagesResponse.loading || getTowersResponse.loading || loading) {
    return <Loader sizeClass={"size--medium"} />;
  }

  if (getPackagesResponse.error || getTowersResponse.error) {
    return (
      <div className="basic-modal">
        <div className="basic-modal__header">
          <h3>Error Loading Data</h3>
        </div>
        <div className="basic-modal__body">
          <div
            className="input-container__error"
            style={{
              padding: "20px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
            }}
          >
            {getPackagesResponse.error?.message ||
              getTowersResponse.error?.message ||
              "Failed to load packages or towers. Please try again."}
          </div>
          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (data) => {
    const clientData = {
      channel: data.channel ? parseInt(data.channel) : null,
      name: data.first_name,
      zonke_id: data?.zonke_id?.value,
      sa_id: data.sa_id,
      surname: data.surname,
      w3w: data.address,
      deposit_paid: data?.deposit_paid?.value,
      package_id: parseInt(data?.package_id?.value),
      phone_number: data.phone_number,
      contact_method: data?.contact_method?.value,
      balance: 0,
      expiry_date: data.expiry_date,
      is_host: data?.is_host?.value,
      active: true,
      client_id: data.client_id ? parseInt(data.client_id) : null,
    };
    try {
      setErrorMessage("");
      const { data } = await createClient({
        variables: { clientData: clientData },
      });
      toastFunction(
        `Successfully created client with name ${data.createClient.client.name}.`,
        "success",
        "client-create-success"
      );
      onCancel();

      reset();
    } catch (error) {
      console.error("Create client error:", error);

      // Extract error message from GraphQL errors or network errors
      let displayError = "Failed to create client. Please try again.";

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0].message;

        // Clean up database error messages for better readability
        if (graphQLError.includes("Duplicate entry")) {
          const match = graphQLError.match(/Duplicate entry '(.+?)' for key/);
          if (match) {
            const parts = match[1].split("-");
            displayError = `This tower (${parts[0]}) and channel (${parts[1]}) combination is already assigned to another client.`;
          } else {
            displayError =
              "This tower and channel combination already exists for another client.";
          }
        } else {
          displayError = graphQLError;
        }
      } else if (error.networkError) {
        displayError = `Network error: ${error.networkError.message}`;
      } else if (error.message) {
        displayError = error.message;
      }

      setErrorMessage(displayError);
      errorToastFunction(displayError, "client-create-error");
    }
  };

  return (
    <div className="basic-modal">
      <div className="basic-modal__header">
        <h3>Add Client</h3>
      </div>
      <div className="basic-modal__body">
        {errorMessage && (
          <div
            className="input-container__error"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
              fontSize: "14px",
              lineHeight: "1.5",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {errorMessage}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="basic-modal__form">
          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"first_name"}
            register={register}
            rules={{ required: "The first name field is required." }}
            placeholder={"First Name"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"number"}
            className={""}
            disabled={false}
            name={"client_id"}
            register={register}
            rules={{
              required: false,
              min: {
                value: 100000,
                message: "Client ID must be at least 100000",
              },
              max: {
                value: 999999,
                message: "Client ID must be at most 999999",
              },
            }}
            placeholder={"Client ID (Optional)"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"surname"}
            register={register}
            rules={{ required: "The surname field is required." }}
            placeholder={"Surname"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"number"}
            className={""}
            disabled={false}
            name={"sa_id"}
            register={register}
            rules={{
              required: "The South African ID field is required.",
              pattern: {
                value: /^[0-9]{13}$/,
                message: "The South African ID must be a 13-digit number.",
              },
            }}
            placeholder={"South African ID"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"address"}
            register={register}
            rules={{ required: false }}
            placeholder={"Address"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"number"}
            className={""}
            disabled={false}
            name={"phone_number"}
            register={register}
            rules={{ required: false }}
            placeholder={"Phone Number"}
            errors={errors}
          />

          <div className="input-container">
            <DisplaySelect
              placeholder={"Tower"}
              name={"zonke_id"}
              errors={errors}
            >
              <Controller
                name={"zonke_id"}
                rules={{
                  required: "The Tower field is required.",
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect field={field} options={towers} />
                )}
              />
            </DisplaySelect>
          </div>

          <FloatingLabelInput
            type={"number"}
            className={""}
            disabled={false}
            name={"channel"}
            register={register}
            rules={{
              required: false,
              min: {
                value: 1,
                message: "Value must be greater than or equal to 1",
              },
              max: {
                value: 20,
                message: "Value must be less than 20.",
              },
            }}
            placeholder={"Channel"}
            errors={errors}
          />

          <div className="input-container">
            <DisplaySelect
              placeholder={"Is Host"}
              name={"is_host"}
              errors={errors}
            >
              <Controller
                name={"is_host"}
                rules={{
                  required: false,
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect
                    field={field}
                    options={[
                      { value: false, label: "No" },
                      { value: true, label: "Yes" },
                    ]}
                  />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="input-container">
            <DisplaySelect
              placeholder={"Deposit Paid"}
              name={"deposit_paid"}
              errors={errors}
            >
              <Controller
                name={"deposit_paid"}
                rules={{
                  required: false,
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect
                    field={field}
                    options={[
                      { value: 0, label: "No" },
                      { value: 1, label: "Yes" },
                    ]}
                  />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="input-container">
            <DisplaySelect
              placeholder={"Package"}
              name={"package_id"}
              errors={errors}
            >
              <Controller
                name={"package_id"}
                rules={{
                  required: "The Package field is required.",
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect field={field} options={packages} />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="input-container">
            <DisplaySelect
              placeholder={"Contact Method"}
              name={"contact_method"}
              errors={errors}
            >
              <Controller
                name={"contact_method"}
                rules={{
                  required: "The contact method field is required.",
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect
                    field={field}
                    options={[
                      { value: "app", label: "Mobile App" },
                      { value: "sms", label: "SMS" },
                    ]}
                  />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Cancel
            </button>
            <button type="submit" className="button button--primary">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClient;
